import { useQuery } from '@tanstack/react-query';
import { Link } from 'react-router-dom';
import { clientsApi, projectsApi } from '../api';
import { Building2, FolderKanban, Plus, ArrowRight } from 'lucide-react';
import { useCanEdit } from '../auth';
import { usePublicSettings } from '../hooks/usePublicSettings';

export function DashboardPage() {
  const canEdit = useCanEdit();
  const { get } = usePublicSettings();
  const recentItems = get<number>('general.dashboardRecentItems');

  const { data: clientsData, isLoading: clientsLoading } = useQuery({
    queryKey: ['clients', { limit: recentItems }],
    queryFn: () => clientsApi.list({ limit: recentItems, active: true }),
  });

  const { data: projectsData, isLoading: projectsLoading } = useQuery({
    queryKey: ['projects', { limit: recentItems }],
    queryFn: () => projectsApi.list({ limit: recentItems }),
  });
  
  const clients = clientsData?.data.data.clients || [];
  const projects = projectsData?.data.data.projects || [];
  const totalClients = clientsData?.data.data.pagination.total || 0;
  const totalProjects = projectsData?.data.data.pagination.total || 0;
  
  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-2xl font-bold text-gray-900">Dashboard</h1>
        <p className="text-gray-600">Panoramica generale</p>
      </div>
      
      {/* Stats */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        <div className="card p-6">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-gray-500">Clienti attivi</p>
              <p className="text-3xl font-bold text-gray-900">{totalClients}</p>
            </div>
            <div className="w-12 h-12 bg-blue-100 rounded-lg flex items-center justify-center">
              <Building2 className="w-6 h-6 text-blue-600" />
            </div>
          </div>
        </div>
        
        <div className="card p-6">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-gray-500">Progetti totali</p>
              <p className="text-3xl font-bold text-gray-900">{totalProjects}</p>
            </div>
            <div className="w-12 h-12 bg-green-100 rounded-lg flex items-center justify-center">
              <FolderKanban className="w-6 h-6 text-green-600" />
            </div>
          </div>
        </div>
      </div>
      
      {/* Recent clients */}
      <div className="card">
        <div className="px-6 py-4 border-b border-gray-200 flex items-center justify-between">
          <h2 className="font-semibold text-gray-900">Clienti recenti</h2>
          <div className="flex items-center space-x-2">
            {canEdit && (
              <Link to="/clients/new" className="btn-primary text-xs py-1.5 px-3">
                <Plus className="w-4 h-4 mr-1" />
                Nuovo
              </Link>
            )}
            <Link to="/clients" className="text-sm text-primary-600 hover:text-primary-700 flex items-center">
              Vedi tutti
              <ArrowRight className="w-4 h-4 ml-1" />
            </Link>
          </div>
        </div>
        
        {clientsLoading ? (
          <div className="p-6 text-center text-gray-500">Caricamento...</div>
        ) : clients.length === 0 ? (
          <div className="p-6 text-center text-gray-500">Nessun cliente</div>
        ) : (
          <ul className="divide-y divide-gray-200">
            {clients.map((client) => (
              <li key={client.id}>
                <Link
                  to={`/clients/${client.id}`}
                  className="block px-6 py-4 hover:bg-gray-50 transition-colors"
                >
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="font-medium text-gray-900">{client.name}</p>
                      <p className="text-sm text-gray-500">{client.code}</p>
                    </div>
                    <ArrowRight className="w-4 h-4 text-gray-400" />
                  </div>
                </Link>
              </li>
            ))}
          </ul>
        )}
      </div>
      
      {/* Recent projects */}
      <div className="card">
        <div className="px-6 py-4 border-b border-gray-200 flex items-center justify-between">
          <h2 className="font-semibold text-gray-900">Progetti recenti</h2>
          <div className="flex items-center space-x-2">
            {canEdit && (
              <Link to="/projects/new" className="btn-primary text-xs py-1.5 px-3">
                <Plus className="w-4 h-4 mr-1" />
                Nuovo
              </Link>
            )}
            <Link to="/projects" className="text-sm text-primary-600 hover:text-primary-700 flex items-center">
              Vedi tutti
              <ArrowRight className="w-4 h-4 ml-1" />
            </Link>
          </div>
        </div>
        
        {projectsLoading ? (
          <div className="p-6 text-center text-gray-500">Caricamento...</div>
        ) : projects.length === 0 ? (
          <div className="p-6 text-center text-gray-500">Nessun progetto</div>
        ) : (
          <ul className="divide-y divide-gray-200">
            {projects.map((project) => {
              const client = typeof project.clientId === 'object' ? project.clientId : null;
              return (
                <li key={project.id}>
                  <Link
                    to={`/projects/${project.id}`}
                    className="block px-6 py-4 hover:bg-gray-50 transition-colors"
                  >
                    <div className="flex items-center justify-between">
                      <div>
                        <p className="font-medium text-gray-900">{project.name}</p>
                        <p className="text-sm text-gray-500">
                          {client?.name || 'Cliente'}
                          <span className="mx-2">•</span>
                          <span className={
                            project.status === 'exported' ? 'text-green-600' :
                            project.status === 'valid' ? 'text-blue-600' :
                            'text-gray-500'
                          }>
                            {project.status}
                          </span>
                        </p>
                      </div>
                      <ArrowRight className="w-4 h-4 text-gray-400" />
                    </div>
                  </Link>
                </li>
              );
            })}
          </ul>
        )}
      </div>
    </div>
  );
}
